import { isEmptyObject } from '@unifyapps/carbon/utils/object';
import type { DrawerBlockStateType } from '@unifyapps/defs/blocks/Drawer/types';
import { isNative } from '@unifyapps/ui/utils/isNative';
import type { JSX } from 'react';
import { useRegistryContext } from '../../context/RegistryProvider';
import type { BlockRendererProps } from '../../context/RegistryProvider/types';
import { useBlockAttributesAndEventListeners } from '../../hooks/useBlockAttributesAndEventListeners';
import useDerivedEvents from '../../hooks/useDerivedEvents';
import useGetBlockBehavior from '../../hooks/useGetBlockBehavior';
import { useSetBlockRef } from '../../hooks/useSetBlockRef';
import { useInterfaceStore } from '../../stores/InterfaceStore';

export function useBlockRenderBody({
  block,
  blockState,
  updateBlockState,
  updateBlockSessionState,
  computedBlockState,
  getGlobalStoreContext,
  dndParams,
  dataAttributes: propsDataAttributes,
}: BlockRendererProps): JSX.Element | null {
  const { id, component, dataSourceIds, events, fallback } = block;
  const { registry } = useRegistryContext();
  const Block = registry.getBlockComponent(component.componentType);
  const activeInterfacePageId = useInterfaceStore().hooks.useActiveInterfacePageId();
  const { blockRef } = useSetBlockRef({
    blockState,
  });

  const isDetachedBlock = useGetBlockBehavior(component.componentType)?.detachFromLayout;

  const blockStateId = blockState.id;
  const { derivedEvents: allEvents } = useDerivedEvents({
    blockStateId,
    events,
  });

  const attributesAndEventListeners = useBlockAttributesAndEventListeners({
    id,
    component,
    pageId: activeInterfacePageId,
    //blockStateId will be different from blockId in case of runtime blocks
    blockStateId,
    computedBlockState,
    propsDataAttributes,
    dndParams,
    blockVersion: block.v,
    // Slot-root marker: set by `useMaterializeSlot` on the consumer's bridged
    // Stack. Pumps `data-slot-root` onto the block's DOM so canvas hover/click
    // can distinguish "inside a slot subtree" from "inside a TC's own inner
    // page" and pick the real clicked block instead of the enclosing TC.
    isSlotRoot: Boolean((block.additional as { isSlotRoot?: boolean } | undefined)?.isSlotRoot),
  });

  // Permission/visibility/empty-block gating happens upstream in
  // `useBlockRendererCore`; by the time we reach this hook the block is
  // permissible, visible, and has a `blockState`. Detached blocks (Drawer-like)
  // still need a runtime check here because they participate in mount lifecycle
  // independently of visibility.
  if (component.componentType === 'BottomSheet' && isNative()) {
    // Keep bottomSheet mounted in native environment
  } else if (component.componentType === 'Modal' && isNative()) {
    // Keep Modal mounted in native environment to allow closing to complete
    // The ModalView component handles unmounting after animation completes
  } else if (
    isDetachedBlock &&
    !isEmptyObject(blockState) &&
    !(blockState as unknown as DrawerBlockStateType).state?.open
  ) {
    return null;
  }

  if (!Block) {
    console.error(
      'Component type not recognized: ',
      component,
      ' in BlocksRegistry ',
      registry.getRegistryName(),
    );
    return null; // or some fallback UI
  }

  // Suspense boundaries were removed to fix 100% CPU utilization issue for few seconds after page load in preference management app
  // It was only happening for SSR pages
  // (https://qa.unifyapps.com/p/0/interfaces/preference-mngmnt/overview?e_interface_q=pref)
  // The exact reason why removing suspense boundaries fixes this issue is still unclear
  return (
    <Block
      attributesAndEventListeners={attributesAndEventListeners}
      block={block}
      blockRef={blockRef}
      blockState={blockState}
      component={component}
      computedBlockState={computedBlockState}
      dataSourceIds={dataSourceIds}
      dndRef={dndParams?.ref}
      events={allEvents}
      fallback={fallback}
      getGlobalStoreContext={getGlobalStoreContext}
      key={id}
      registry={registry}
      updateBlockSessionState={updateBlockSessionState}
      updateBlockState={updateBlockState}
    />
  );
}
